import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useLogo, LANGUAGES, Flag, TRANSLATIONS } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Clock, ArrowLeft, Eye, EyeOff, Mail, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { logoUrl } = useLogo();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedLang, setSelectedLang] = useState("ja");
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  // Get translations
  const t = TRANSLATIONS[selectedLang] || TRANSLATIONS.ja;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(loginEmail, loginPassword);
      
      if (result.success) {
        toast.success("ログイン成功");
        navigate("/admin");
      } else {
        // Check if verification is required
        if (result.requiresVerification) {
          toast.error("メールアドレスが未認証です");
          navigate(`/verify-email?email=${encodeURIComponent(loginEmail)}`);
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error("ログインに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: regName,
        email: regEmail,
        password: regPassword,
        role: "admin"
      });
      
      if (res.data.requires_verification) {
        toast.success(res.data.message);
        // Redirect to verification page
        navigate(`/verify-email?email=${encodeURIComponent(regEmail)}`);
      } else {
        toast.success("登録成功");
        navigate("/admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "登録に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50" ref={langDropdownRef}>
        <button
          onClick={() => setLangDropdownOpen(!langDropdownOpen)}
          className="flex items-center gap-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          <Flag countryCode={LANGUAGES.find(l => l.code === selectedLang)?.countryCode || 'jp'} size={18} />
          <span className="hidden sm:inline">{LANGUAGES.find(l => l.code === selectedLang)?.name}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        {langDropdownOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => { setSelectedLang(lang.code); setLangDropdownOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-100 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg ${selectedLang === lang.code ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                <Flag countryCode={lang.countryCode} size={18} />
                <span className="text-sm">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1705909773171-4ba952b9c0af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMG1pbmltYWx8ZW58MHx8fHwxNzczNDU1OTEwfDA&ixlib=rb-4.1.0&q=85"
          alt="Office"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 to-slate-900/50 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <img src={logoUrl} alt="PRC勤怠" className="w-20 h-20 mx-auto mb-6 object-contain rounded-2xl" />
            <h1 className="text-4xl font-bold mb-4">{t.landingTitle}</h1>
            <p className="text-lg text-slate-300 max-w-md">
              {t.landingSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-3 mb-2 lg:hidden">
                <img src={logoUrl} alt="PRC勤怠" className="w-10 h-10 object-contain rounded-xl" />
                <span className="font-bold text-xl">{t.landingTitle}</span>
              </div>
              <CardTitle className="text-2xl">{t.login}</CardTitle>
              <CardDescription>{selectedLang === "ja" ? "アカウントでログインまたは新規登録" : selectedLang === "en" ? "Login or register your account" : selectedLang === "pt" ? "Entre ou registre sua conta" : selectedLang === "vi" ? "Đăng nhập hoặc đăng ký tài khoản" : selectedLang === "tl" ? "Mag-login o mag-register" : "Iniciar sesión o registrarse"}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">{t.login}</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">{t.register}</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t.email}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="admin@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t.password}</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary" 
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t.loading}
                        </>
                      ) : (
                        t.loginButton
                      )}
                    </Button>
                  </form>
                  
                  {/* Link to verify email */}
                  <div className="mt-4 text-center">
                    <Link 
                      to="/verify-email"
                      className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      data-testid="goto-verify-email"
                    >
                      <Mail className="w-4 h-4 inline mr-1" />
                      {selectedLang === "ja" ? "認証コードを入力する" : selectedLang === "en" ? "Enter verification code" : selectedLang === "pt" ? "Inserir código de verificação" : selectedLang === "vi" ? "Nhập mã xác minh" : selectedLang === "tl" ? "Ilagay ang verification code" : "Ingresar código de verificación"}
                    </Link>
                  </div>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">{selectedLang === "ja" ? "名前" : selectedLang === "en" ? "Name" : selectedLang === "pt" ? "Nome" : selectedLang === "vi" ? "Tên" : selectedLang === "tl" ? "Pangalan" : "Nombre"}</Label>
                      <Input
                        id="reg-name"
                        type="text"
                        placeholder={selectedLang === "ja" ? "管理者 太郎" : "Admin User"}
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">{t.email}</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="admin@example.com"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">{t.password}</Label>
                      <div className="relative">
                        <Input
                          id="reg-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary" 
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t.loading}
                        </>
                      ) : (
                        t.register
                      )}
                    </Button>
                    
                    <p className="text-xs text-slate-500 text-center">
                      {selectedLang === "ja" ? "登録後、メールアドレス認証が必要です" : selectedLang === "en" ? "Email verification required after registration" : selectedLang === "pt" ? "Verificação de email necessária após o registro" : selectedLang === "vi" ? "Yêu cầu xác minh email sau khi đăng ký" : selectedLang === "tl" ? "Kailangan ng email verification pagkatapos mag-register" : "Se requiere verificación de email después del registro"}
                    </p>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            {selectedLang === "ja" ? "従業員の方は" : selectedLang === "en" ? "Employees can" : selectedLang === "pt" ? "Funcionários podem" : selectedLang === "vi" ? "Nhân viên có thể" : selectedLang === "tl" ? "Mga empleyado" : "Los empleados pueden"}{" "}
            <Link to="/face-auth" className="text-slate-900 dark:text-white hover:underline" data-testid="goto-face-auth">
              {t.faceAuth}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
